import { Injectable } from '@angular/core';
import { ShowInactiveService } from '../../base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from '../../base/shared/syncfusion/adaptor-service.interface';
import { interval, Observable, of, throwError } from 'rxjs';
import {
  ExecuteZoneDeviceCommandDTO,
  FilterCondition,
  FilterDTO,
  FilterOperator,
  PagedResultDTOOfZoneDeviceDTO,
  SortDirection,
  ZoneDeviceClient,
  ZoneDeviceDTO,
} from '../../base/models/models.swagger.generated';
import { catchError, map, switchMap, filter, take } from 'rxjs/operators';
import { ErrorType } from '../../core/routing/error-types';
import { ToastService } from '../../base/services/toast.service';

@Injectable()
export class DeviceInteractionService extends ShowInactiveService implements IAdaptorService {

  public clientId: number;
  public zoneIds: number[];

  constructor(private toastService: ToastService,
              private zoneDeviceClient: ZoneDeviceClient) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfZoneDeviceDTO> {
    if (!this.clientId) {
      return of(new PagedResultDTOOfZoneDeviceDTO({items: [], totalNumberOfItems: 0}));
    }

    if (!filters) {
      filters = [];
    }
    filters.push(new FilterDTO({isComplex: false, field: 'Enabled', operator: FilterOperator.Equal, value: true.toString()}));

    if (this.zoneIds && this.zoneIds.length > 0) {
      filters.push(new FilterDTO({
        isComplex: true,
        condition: FilterCondition.Or,
        predicates: this.zoneIds.map( zoneId =>
          new FilterDTO({ isComplex: false, field: 'ZoneId', operator: FilterOperator.Equal, value: zoneId.toString()})
        )
      }));
    }

    return this.zoneDeviceClient.searchDevices(this.clientId, filters, 0, 0, sortBy, sortDirection)
      .pipe(
          map(pagedResult => {
            const items = this.filterAndMapDevices(pagedResult.items);
            if (skip != null && take != null) {
              return new PagedResultDTOOfZoneDeviceDTO({
                items: items.slice(skip, skip + take),
                totalNumberOfItems: items.length,
              });
            }
            return new PagedResultDTOOfZoneDeviceDTO({
              items,
              totalNumberOfItems: items.length,
            });
          }),
      );
  }

  public addRecord(data: any): Observable<any> {
    return throwError(() => new Error('Not implemented'));
  }

  public deleteRecord(id: number): Observable<any> {
    return throwError(() => new Error('Not implemented'));
  }

  public updateRecord(data: any): Observable<any> {
    return throwError(() => new Error('Not implemented'));
  }

  public executeDeviceCommand(dto: ExecuteZoneDeviceCommandDTO): Observable<void> {
    return this.zoneDeviceClient.executeDeviceCommand(dto)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  public observeCommandStatus(deviceId: number, origPosition: string): Observable<ZoneDeviceDTO> {
    let cnt = 0;
    return interval(1000).pipe(
      switchMap(() => this.zoneDeviceClient.getDevice(deviceId)),
      map(device => {
        cnt++;
        return { device, shouldStop: (device.currentDevicePosition !== 'Pending' && device.currentDevicePosition !== origPosition) || cnt > 10 } as { device: ZoneDeviceDTO; shouldStop: boolean };
      }),
      filter((val: { device: ZoneDeviceDTO; shouldStop: boolean }) => val.shouldStop),
      map((val: { device: ZoneDeviceDTO; shouldStop: boolean }) => val.device),
      take(1)
    );
  }

  public getAllCommandableDevicesForCurrentUser(): Observable<ZoneDeviceDTO[]> {
    return this.zoneDeviceClient.getAllCommandableDevicesForCurrentUser()
      .pipe(
        map(items => this.filterAndMapDevices(items)),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  /**
   * Filters devices and maps supportedCommands for each device.
   */
  private filterAndMapDevices(devices: ZoneDeviceDTO[]): ZoneDeviceDTO[] {
    const items = devices.filter(i => !!i.supportedDevicePositions
      && i.supportedDevicePositions.length > 0
      && i.supportedDevicePositions.find(p => !!p.supportedCommands && p.supportedCommands.length > 0));

    items.forEach(i => {
      let dev = (i as any);
      dev.supportedCommands = i.supportedDevicePositions.find(p => p.position === i.currentDevicePosition)?.supportedCommands;
      if (!dev.supportedCommands && i.currentDevicePosition == null) {
        // look for 'unknown' position commands as a fallback
        dev.supportedCommands = i.supportedDevicePositions.find(p => p.position.toLowerCase() === 'unknown')?.supportedCommands;
      }
      // make sure supported commands is not null
      if (!dev.supportedCommands) {
        dev.supportedCommands = [];
      }
    });
    return items;
  }
}
